import React from 'react';
import { SmartOnboarding } from 'redpoints-front-smart-onboarding-rp10/SmartOnboarding';
import type { AnyAction } from 'redux';

import { NotFound } from 'redpoints-front-common-rp10/appLoader';
import { PERMISSIONS } from 'redpoints-front-common-rp10/authorization';
import { withPermissionCheck } from 'redpoints-front-common-rp10/components';
import type { Route } from 'redpoints-front-common-rp10/types';
import { AccountDetailsPage } from 'redpoints-front-actors-rp10/AccountDetails';
import { ActorNetworksPage } from 'redpoints-front-actors-rp10/ActorNetworks';
import { ActorsPage } from 'redpoints-front-actors-rp10/Actors';
import { ACTORS_CLEAR } from 'redpoints-front-actors-rp10/Actors/actions/actionTypes';
import { NewSellerManagementPage } from 'redpoints-front-actors-rp10/NewSellerManagement';
import { SellerDetailsPage as SellerDetails } from 'redpoints-front-actors-rp10/SellerDetails';
import { SellerGroupDetailsPage as SellerGroupDetails } from 'redpoints-front-actors-rp10/SellerGroupDetails';
import { SellerManagementPage } from 'redpoints-front-actors-rp10/SellerManagement';
import { RuleSuggestionsPage } from 'redpoints-front-configuration-rp10/RuleSuggestions';
import { TagsManagementPage } from 'redpoints-front-configuration-rp10/TagsManagement';
import { TemplatesListPage } from 'redpoints-front-configuration-rp10/Templates';
import { TemplatesFormPage } from 'redpoints-front-configuration-rp10/Templates';
import {
  Database,
  DatabaseIncidentDetails,
} from 'redpoints-front-database-rp10/Database';
import { DATABASE_CLEAR } from 'redpoints-front-database-rp10/Database/actions/actionTypes';
import { Documents } from 'redpoints-front-documents-rp10/DocumentsRepository';
import { Journey } from 'redpoints-front-everafter-rp10/Journey';
import { HomePage } from 'redpoints-front-home-rp10/Home';
import { IncidentDetailsDatabasePage } from 'redpoints-front-incidents-rp10/IncidentDetailsDatabase';
import { IncidentDetailsValidationPage } from 'redpoints-front-incidents-rp10/IncidentDetailsValidation';
import { NewIncidentPage } from 'redpoints-front-incidents-rp10/NewIncident';
import { AssetVerification } from 'redpoints-front-operations-rp10/AssetVerification';
import { CardDetail } from 'redpoints-front-operations-rp10/CardDetail';
import { EnforcementAudit } from 'redpoints-front-operations-rp10/EnforcementAudit';
import { InfringementEvaluation } from 'redpoints-front-operations-rp10/InfringementEvaluation';
import {
  AntipiracySummaryPage,
  CustomReportsPage,
  EconomicImpactPage,
  MyLinksAndDocsPage,
  NonEnforceablePage,
  ResultAnalysisPage,
  SellersPage,
  SocialMediaImpactPage,
  SummaryPage,
  WebsiteInfringementsPage,
} from 'redpoints-front-reports-rp10/Results';
import { SuccessPlansPage } from 'redpoints-front-success-plans-rp10/SuccessPlans';
import {
  Validation,
  ValidationIncidentDetails,
} from 'redpoints-front-validation-rp10/Validation';
import { VALIDATION_CLEAR } from 'redpoints-front-validation-rp10/Validation/actions/actionTypes';

import DomainManagement from './components/DomainManagement';
import IpOfflineServices from './components/IpOfflineServices';
import RevenueRecovery from './components/RevenueRecovery';
import { ROUTES } from './config/constants';

const clearProps = {
  actorsClear: (): AnyAction => ({ type: ACTORS_CLEAR }),
  databaseClear: (): AnyAction => ({ type: DATABASE_CLEAR }),
  validationClear: (): AnyAction => ({ type: VALIDATION_CLEAR }),
};

const validationIncidentDetailsProps = {
  IncidentDetailsPage: IncidentDetailsValidationPage,
};

const databaseIncidentDetailsProps = {
  IncidentDetailsPage: IncidentDetailsDatabasePage,
};

const routeJourney = {
  component: Journey,
  exact: true,
  path: ROUTES.JOURNEY,
  permissionEntity: PERMISSIONS.PORTAL_JOURNEY_READ_AUTH.entity,
};

const routeHome = {
  component: HomePage,
  exact: true,
  path: ROUTES.HOME,
  permissionEntity: PERMISSIONS.PORTAL_HOME_READ_AUTH.entity,
};

const routeNewIncident = {
  component: NewIncidentPage,
  exact: true,
  path: ROUTES.NEW_INCIDENT,
  permissionEntity: PERMISSIONS.PORTAL_NEW_INCIDENT_READ_AUTH.entity,
};

const routeAssetVerification = {
  component: AssetVerification,
  exact: true,
  path: ROUTES.ASSET_VERIFICATION,
  permissionEntity: PERMISSIONS.PORTAL_ASSET_VERIFICATION_READ_AUTH.entity,
};

const routeAssetVerificationCardDetail = {
  component: CardDetail,
  exact: true,
  path: ROUTES.ASSET_VERIFICATION_CARD_DETAIL,
  permissionEntity: PERMISSIONS.PORTAL_ASSET_VERIFICATION_READ_AUTH.entity,
};

const routeInfringementEvaluation = {
  component: InfringementEvaluation,
  exact: true,
  path: ROUTES.INFRINGEMENT_EVALUATION,
  permissionEntity: PERMISSIONS.PORTAL_INFRINGEMENT_EVALUATION_READ_AUTH.entity,
};

const routeTagsManagement = {
  component: TagsManagementPage,
  exact: true,
  path: ROUTES.TAGS_MANAGEMENT,
  permissionEntity: PERMISSIONS.PORTAL_CONFIGURATION_READ_AUTH.entity,
};

const routesTemplates = [
  {
    component: TemplatesListPage,
    exact: true,
    path: ROUTES.TEMPLATES,
    permissionEntity: PERMISSIONS.PORTAL_TEMPLATES_READ_AUTH.entity,
  },
  {
    component: TemplatesFormPage,
    exact: true,
    path: ROUTES.TEMPLATES_CREATE,
    permissionEntity: PERMISSIONS.PORTAL_TEMPLATES_READ_AUTH.entity,
  },
  {
    component: TemplatesFormPage,
    exact: true,
    path: ROUTES.TEMPLATES_EDIT,
    permissionEntity: PERMISSIONS.PORTAL_TEMPLATES_READ_AUTH.entity,
  },
];

const routeRuleSuggestions = {
  component: RuleSuggestionsPage,
  exact: true,
  path: ROUTES.RULE_SUGGESTIONS,
  permissionEntity:
    PERMISSIONS.PORTAL_CONFIGURATION_SMART_RULES_READ_AUTH.entity,
};

const routesSellerManagement = [
  {
    component: NewSellerManagementPage,
    exact: true,
    path: ROUTES.NEW_SELLER_MANAGEMENT,
    permissionEntity: PERMISSIONS.PORTAL_SELLER_LISTS_READ_AUTH.entity,
  },
  {
    component: SellerManagementPage,
    exact: true,
    path: ROUTES.SELLER_MANAGEMENT,
    permissionEntity: PERMISSIONS.PORTAL_SELLER_LISTS_READ_AUTH.entity,
  },
];

const routesCustomer = [
  {
    component: Documents,
    strict: true,
    path: ROUTES.DOCUMENTS,
    permissionEntity: 'portal_documents',
  },
];

const routeSmartOnboarding = {
  component: SmartOnboarding,
  strict: true,
  path: ROUTES.SMART_ONBOARDING,
  permissionEntity: PERMISSIONS.PORTAL_SMART_ONBOARDING_READ_AUTH.entity,
};

const routesDatabase = [
  {
    component: Database,
    exact: true,
    path: ROUTES.DATABASE,
    permissionEntity: PERMISSIONS.PORTAL_DATABASE_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => (
      <DatabaseIncidentDetails {...databaseIncidentDetailsProps} />
    ),
    exact: true,
    strict: true,
    path: ROUTES.INCIDENT_DETAILS_DATABASE,
    permissionEntity: PERMISSIONS.PORTAL_INCIDENT_DETAIL_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <SellerDetails {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.SELLER_DETAILS_DATABASE,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_SELLER_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <SellerDetails {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.SELLER_DETAILS_DATABASE_INCIDENT_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_SELLER_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <SellerGroupDetails {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.SELLER_GROUP_DETAILS_DATABASE,
    permissionEntity:
      PERMISSIONS.PORTAL_VALIDATION_GROUPED_SELLER_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <AccountDetailsPage {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.ACCOUNT_DETAILS_DATABASE,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_ACCOUNT_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <AccountDetailsPage {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.ACCOUNT_DETAILS_DATABASE_INCIDENT_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_ACCOUNT_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_DATABASE,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_DATABASE_ACCOUNT_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_DATABASE_INCIDENT_DETAILS_ACCOUNT_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_DATABASE_SELLER_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_DATABASE_INCIDENT_DETAILS_SELLER_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
];

const routesValidation = [
  {
    component: Validation,
    exact: true,
    path: ROUTES.VALIDATION,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => (
      <ValidationIncidentDetails {...validationIncidentDetailsProps} />
    ),
    exact: true,
    strict: true,
    path: ROUTES.INCIDENT_DETAILS_VALIDATION,
    permissionEntity: PERMISSIONS.PORTAL_INCIDENT_DETAIL_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <SellerDetails {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.SELLER_DETAILS_VALIDATION,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_SELLER_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <SellerDetails {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.SELLER_DETAILS_VALIDATION_INCIDENT_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_SELLER_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <SellerGroupDetails {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.SELLER_GROUP_DETAILS_VALIDATION,
    permissionEntity:
      PERMISSIONS.PORTAL_VALIDATION_GROUPED_SELLER_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <AccountDetailsPage {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.ACCOUNT_DETAILS_VALIDATION,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_ACCOUNT_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <AccountDetailsPage {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.ACCOUNT_DETAILS_VALIDATION_INCIDENT_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_ACCOUNT_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_VALIDATION,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_VALIDATION_ACCOUNT_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_VALIDATION_INCIDENT_DETAILS_ACCOUNT_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_VALIDATION_SELLER_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_VALIDATION_INCIDENT_DETAILS_SELLER_DETAILS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
];

const routesResults = [
  {
    component: SummaryPage,
    exact: true,
    path: ROUTES.SUMMARY,
    permissionEntity: PERMISSIONS.PORTAL_DASHBOARDS_SUMMARY_READ_AUTH.entity,
  },
  {
    component: SocialMediaImpactPage,
    exact: true,
    path: ROUTES.SOCIAL_MEDIA_IMPACT,
    permissionEntity: PERMISSIONS.PORTAL_IMPERSONATION_READ_AUTH.entity,
  },
  {
    component: AntipiracySummaryPage,
    exact: true,
    path: ROUTES.ANTIPIRACY_SUMMARY,
    permissionEntity: PERMISSIONS.PORTAL_DASHBOARDS_ANTIPIRACY_READ_AUTH.entity,
  },
  {
    component: ResultAnalysisPage,
    exact: true,
    path: ROUTES.RESULT_ANALYSIS,
    permissionEntity:
      PERMISSIONS.PORTAL_DASHBOARDS_RESULT_ANALYSIS_READ_AUTH.entity,
  },
  {
    component: NonEnforceablePage,
    exact: true,
    path: ROUTES.NON_ENFORCEABLE,
    permissionEntity:
      PERMISSIONS.PORTAL_DASHBOARDS_NON_ENFORCEABLE_READ_AUTH.entity,
  },
  {
    component: MyLinksAndDocsPage,
    exact: true,
    path: ROUTES.MY_LINKS_AND_DOCS,
    permissionEntity:
      PERMISSIONS.PORTAL_DASHBOARDS_MY_LINKS_AND_DOCS_READ_AUTH.entity,
  },
  {
    component: SellersPage,
    exact: true,
    path: ROUTES.SELLERS,
    permissionEntity: PERMISSIONS.PORTAL_DASHBOARDS_SELLERS_READ_AUTH.entity,
  },
  {
    component: EconomicImpactPage,
    exact: true,
    path: ROUTES.ECONOMIC_IMPACT,
    permissionEntity:
      PERMISSIONS.PORTAL_DASHBOARDS_ECONOMIC_IMPACT_READ_AUTH.entity,
  },
  {
    component: CustomReportsPage,
    exact: true,
    path: ROUTES.CUSTOM_REPORTS,
    permissionEntity: PERMISSIONS.PORTAL_CUSTOM_REPORTS_READ_AUTH.entity,
  },
  {
    component: WebsiteInfringementsPage,
    exact: true,
    path: ROUTES.WEBSITE_INFRINGEMENTS,
    permissionEntity:
      PERMISSIONS.PORTAL_DASHBOARDS_WEBSITE_INFRINGEMENTS_READ_AUTH.entity,
  },
];

const routeDomainManagement = {
  component: DomainManagement,
  exact: true,
  path: ROUTES.DOMAIN_MANAGEMENT,
  permissionEntity: PERMISSIONS.PORTAL_DOMAIN_MONITORING_READ_AUTH.entity,
};

const routeRevenueRecovery = {
  component: RevenueRecovery,
  exact: true,
  path: ROUTES.REVENUE_RECOVERY,
  permissionEntity: PERMISSIONS.PORTAL_REVENUE_RECOVERY_READ_AUTH.entity,
};

const routeIpOfflineServices = {
  component: IpOfflineServices,
  exact: true,
  path: ROUTES.IP_OFFLINE_SERVICES,
  permissionEntity: PERMISSIONS.PORTAL_IP_OFFLINE_SERVICES_READ_AUTH.entity,
};

const routeEnforcementAudit = {
  component: EnforcementAudit,
  exact: true,
  path: ROUTES.ENFORCEMENT_AUDIT,
  permissionEntity: PERMISSIONS.PORTAL_ENFORCEMENT_AUDIT_READ_AUTH.entity,
};

const routesActors = [
  {
    component: ActorsPage,
    exact: true,
    path: ROUTES.ACTORS,
    permissionEntity: PERMISSIONS.PORTAL_ACTORS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <SellerDetails {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.SELLER_DETAILS_ACTORS,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_SELLER_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <AccountDetailsPage {...clearProps} />,
    exact: true,
    strict: true,
    path: ROUTES.ACCOUNT_DETAILS_ACTORS,
    permissionEntity: PERMISSIONS.PORTAL_VALIDATION_ACCOUNT_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_ACTORS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
  {
    component: (): JSX.Element => <ActorNetworksPage {...clearProps} />,
    exact: true,
    path: ROUTES.ACTOR_NETWORKS_NETWORKS,
    permissionEntity: PERMISSIONS.PORTAL_ACTOR_NETWORKS_READ_AUTH.entity,
  },
];

const routeSuccessPlans = {
  component: SuccessPlansPage,
  exact: true,
  path: ROUTES.SUCCESS_PLANS,
  permissionEntity: PERMISSIONS.PORTAL_SUCCESS_PLANS_READ_AUTH.entity,
};

const routes: Route[] = [
  routeJourney,
  routeHome,
  routeNewIncident,
  routeAssetVerification,
  routeAssetVerificationCardDetail,
  routeInfringementEvaluation,
  ...routesSellerManagement,
  ...routesDatabase,
  ...routesValidation,
  routeEnforcementAudit,
  ...routesActors,
  routeSuccessPlans,
  ...routesResults,
  routeTagsManagement,
  routeRuleSuggestions,
  ...routesCustomer,
  ...routesTemplates,
  routeSmartOnboarding,
  routeDomainManagement,
  routeIpOfflineServices,
  routeRevenueRecovery,
].map(({ component, permissionEntity, ...route }) => ({
  ...route,
  permissionEntity: permissionEntity ?? undefined,
  component: withPermissionCheck(component),
}));

routes.push(
  {
    component: (): null => null,
    path: ROUTES.ROOT,
    exact: true,
  },
  {
    component: NotFound,
  }
);

export default routes;
